import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import WorkBankMap from "@/components/WorkBankMap";
import S3Uploader from "@/components/S3Uploader";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import {
  Briefcase, List, Map as MapIcon, Columns, Plus, Search, X, Loader2, CalendarClock, RefreshCw, MapPinOff,
  Building2, ClipboardPlus, ChevronDown, Calendar as CalendarIcon, Paperclip,
} from "lucide-react";

const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function DatePicker({ value, onChange, testid, placeholder = "Pick a date" }) {
  const date = value ? new Date(value + "T00:00:00") : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={testid}
          className={`w-full justify-start text-left font-normal h-9 ${!value ? "text-slate-400" : "text-slate-700"}`}>
          <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
          {value ? format(date, "d MMM yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={(d) => onChange(d ? toISODate(d) : "")} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

const PRIO = ["Low", "Medium", "High", "Urgent"];
const PLANNING = ["Unscheduled", "Scheduled", "In Progress", "Completed", "On Hold", "Cancelled"];
const STATUSES = ["New", "Ready to Schedule", "Scheduled", "In Progress", "Completed", "On Hold", "Cancelled"];
const STATUS_COLOUR = {
  New: "bg-slate-100 text-slate-700", "Ready to Schedule": "bg-indigo-100 text-indigo-700",
  Unscheduled: "bg-slate-100 text-slate-700",
  Scheduled: "bg-blue-100 text-blue-700", "In Progress": "bg-amber-100 text-amber-700",
  Completed: "bg-emerald-100 text-emerald-700", "On Hold": "bg-orange-100 text-orange-700",
  Cancelled: "bg-rose-100 text-rose-700",
};

function Card({ label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm" data-testid={testid}>
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function WorkBank() {
  const [view, setView] = useState("split");
  const [items, setItems] = useState([]);
  const [geo, setGeo] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [sources, setSources] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState(null);
  const [staff, setStaff] = useState([]);
  const [sched, setSched] = useState(null);
  const [addExisting, setAddExisting] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const p = { q: q || undefined };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") p[k] = v; });
    return p;
    // eslint-disable-next-line
  }, [q, JSON.stringify(filters)]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/work-bank", { params: { ...params, page_size: 100 } }),
      api.get("/work-bank/geo", { params }),
      api.get("/work-bank/summary"),
    ]).then(([l, g, s]) => {
      setItems(l.data.items || []); setTotal(l.data.total || 0);
      setGeo(g.data.items || []); setSummary(s.data || {}); setSources(s.data?.sources || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then((r) => setStaff(r.data.items || r.data.users || r.data || [])).catch(() => {});
  }, []);
  // Deep-links from Job detail: ?open=task:<id> opens a planning entry, ?add_job=<legacy> opens Add Existing
  useEffect(() => {
    const open = searchParams.get("open");
    const addJob = searchParams.get("add_job");
    if (open) { openItem(open); searchParams.delete("open"); setSearchParams(searchParams, { replace: true }); }
    else if (addJob) { setAddExisting({ legacy_id: addJob }); searchParams.delete("add_job"); setSearchParams(searchParams, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const onMapSelect = (ids) => setSelected(new Set(ids));
  const clearSel = () => setSelected(new Set());
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const bulk = async (action, value) => {
    try {
      await api.post("/work-bank/bulk", { ids: [...selected], action, value });
      toast.success("Updated"); clearSel(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
  };

  const doSchedule = async () => {
    try {
      await api.post("/work-bank/schedule", { ids: [...selected], date: sched.date, start_time: sched.start_time, user: sched.user || null });
      toast.success(`Scheduled ${selected.size} item(s)`); setSched(null); clearSel(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Schedule failed"); }
  };

  const openItem = async (id) => {
    try { const r = await api.get(`/work-bank/${id}`); setForm(r.data); }
    catch { toast.error("Could not open item"); }
  };
  const refresh = async () => {
    try { await api.post("/work-bank/rebuild-index"); toast.success("Job Pool refreshed from live jobs"); load(); }
    catch { toast.error("Refresh failed"); }
  };
  const chip = (active) => `h-8 px-3 rounded-full text-sm border transition-colors ${active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`;

  const estHours = geo.reduce((a, p) => a + (p.estimated_minutes || 0), 0);
  const selMins = items.filter((i) => selected.has(i.id)).reduce((a, i) => a + (i.estimated_minutes || 0), 0);

  const ListView = (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="wb-list">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left text-xs text-slate-500 border-b border-slate-200">
            <th className="px-3 py-3 w-8"></th><th className="px-3 py-3">Work ID</th><th className="px-3 py-3">Source</th><th className="px-3 py-3">Title</th>
            <th className="px-3 py-3">Customer</th><th className="px-3 py-3">Type</th><th className="px-3 py-3">Priority</th>
            <th className="px-3 py-3">Planning</th><th className="px-3 py-3">Deadline</th><th className="px-3 py-3">Est</th>
          </tr></thead>
          <tbody>
            {!loading && items.length === 0 && <tr><td colSpan={10} className="px-4 py-16 text-center text-slate-400">No work items. Add work to get started.</td></tr>}
            {items.map((w) => (
              <tr key={w.id} onClick={() => openItem(w.id)} className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${selected.has(w.id) ? "bg-emerald-50/60" : ""}`} data-testid={`wb-row-${w.id}`}>
                <td className="px-3 py-3" onClick={(e) => { e.stopPropagation(); toggle(w.id); }}>
                  <input type="checkbox" checked={selected.has(w.id)} onChange={() => {}} data-testid={`wb-check-${w.id}`} />
                </td>
                <td className="px-3 py-3 font-mono text-xs text-slate-500">{w.work_id}</td>
                <td className="px-3 py-3"><span className="text-[11px] font-medium rounded-full px-2 py-0.5 bg-slate-100 text-slate-600 capitalize">{w.source_type}</span></td>
                <td className="px-3 py-3 font-medium text-slate-800">{w.title}{w.geo_status === "missing" && <span title="Map location missing" className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-rose-600"><MapPinOff className="w-3 h-3" /> location</span>}</td>
                <td className="px-3 py-3 text-slate-600">{w.customer_name || "—"}</td>
                <td className="px-3 py-3 text-slate-600">{w.work_type || "—"}</td>
                <td className="px-3 py-3">{w.priority}</td>
                <td className="px-3 py-3"><span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${STATUS_COLOUR[w.planning_status] || "bg-slate-100"}`}>{w.planning_status}</span></td>
                <td className="px-3 py-3 text-slate-600">{w.deadline_date || "—"}</td>
                <td className="px-3 py-3 text-slate-500">{w.estimated_minutes ? `${w.estimated_minutes}m` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const MapView = (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden h-[70vh]" data-testid="wb-map-wrap">
      <WorkBankMap points={geo} selectedIds={selected} onSelect={onMapSelect} onOpen={(id) => { const w = items.find((i) => i.id === id); if (w) setForm(w); }} />
    </div>
  );

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="page-work-bank">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <PageIcon />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Job Pool</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} work items · unscheduled & unallocated</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {[["list", List], ["map", MapIcon], ["split", Columns]].map(([v, Ic]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`wb-view-${v}`}
                className={`px-3 py-2 ${view === v ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}><Ic className="w-4 h-4" /></button>
            ))}
          </div>
          <Button variant="outline" className="gap-1.5" onClick={refresh} data-testid="wb-refresh-btn"><RefreshCw className="w-4 h-4" /> Refresh</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="wb-add-btn"><Plus className="w-4 h-4" /> Add Work <ChevronDown className="w-4 h-4 opacity-80" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem onClick={() => setAddExisting({})} data-testid="wb-add-existing-btn">
                <Building2 className="w-4 h-4 mr-2 text-indigo-600" /> <div><div className="font-medium">Add Existing Job</div><div className="text-[11px] text-slate-500">Reference a Job — never duplicated</div></div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setForm({})} data-testid="wb-add-standalone-btn">
                <ClipboardPlus className="w-4 h-4 mr-2 text-emerald-600" /> <div><div className="font-medium">Create Standalone Work</div><div className="text-[11px] text-slate-500">Ad-hoc work with no existing Job</div></div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <Card label="Unallocated Work" value={summary.unallocated ?? 0} testid="wb-card-unallocated" />
        <Card label="Due Today" value={summary.due_today ?? 0} tone="text-amber-600" testid="wb-card-today" />
        <Card label="Overdue" value={summary.overdue ?? 0} tone="text-rose-600" testid="wb-card-overdue" />
        <Card label="This Week" value={summary.this_week ?? 0} testid="wb-card-week" />
        <Card label="Estimated Hours" value={`${summary.estimated_hours ?? 0}h`} tone="text-indigo-600" testid="wb-card-hours" />
      </div>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="wb-source-chips">
        <button onClick={() => setFilter("source_type", "all")} className={chip(!filters.source_type || filters.source_type === "all")} data-testid="wb-chip-all">All Work</button>
        {sources.map((s) => (
          <button key={s.key} onClick={() => setFilter("source_type", s.key)} className={chip(filters.source_type === s.key)} data-testid={`wb-chip-${s.key}`}>
            {s.label} <span className="opacity-60">{s.count}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search work, customer, postcode…" className="pl-9 h-9 w-64 bg-white" data-testid="wb-search" />
        </div>
        {[["planning_status", "Planning", PLANNING], ["priority", "Priority", PRIO], ["assigned", "Assignment", ["assigned", "unassigned"]]].map(([k, label, opts]) => (
          <Select key={k} value={filters[k] || "all"} onValueChange={(v) => setFilter(k, v)}>
            <SelectTrigger className="h-9 w-auto min-w-[130px] bg-white" data-testid={`wb-filter-${k}`}><SelectValue placeholder={label} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{label}: All</SelectItem>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        ))}
        <Input value={filters.work_type || ""} onChange={(e) => setFilter("work_type", e.target.value)} placeholder="Work type" className="h-9 w-32 bg-white" data-testid="wb-filter-type" />
        <Input value={filters.postcode || ""} onChange={(e) => setFilter("postcode", e.target.value)} placeholder="Postcode" className="h-9 w-28 bg-white" data-testid="wb-filter-postcode" />
        {[["today", "Due Today"], ["overdue", "Overdue"], ["week", "This Week"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter("due", filters.due === v ? "all" : v)} className={`h-9 px-3 rounded-lg text-sm border ${filters.due === v ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`wb-quick-${v}`}>{l}</button>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 bg-indigo-600 text-white rounded-xl px-4 py-2.5" data-testid="wb-bulk-bar">
          <span className="font-semibold">{selected.size} selected</span>
          <span className="text-indigo-200 text-sm">Est. {Math.floor(selMins / 60)}h {selMins % 60}m</span>
          <div className="flex-1" />
          <Select onValueChange={(v) => setConfirmBulk({ action: "status", value: { status: v }, label: `set status to “${v}”` })}><SelectTrigger className="h-8 w-32 bg-white text-slate-700" data-testid="wb-bulk-status"><SelectValue placeholder="Set status" /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <Select onValueChange={(v) => setConfirmBulk({ action: "priority", value: { priority: v }, label: `set priority to “${v}”` })}><SelectTrigger className="h-8 w-28 bg-white text-slate-700" data-testid="wb-bulk-priority"><SelectValue placeholder="Priority" /></SelectTrigger><SelectContent>{PRIO.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <Select onValueChange={(v) => { const u = staff.find((x) => (x.id || x.email) === v); setConfirmBulk({ action: "assign_user", value: { user_id: v, user_name: u?.name || u?.email }, label: `assign to ${u?.name || u?.email}` }); }}><SelectTrigger className="h-8 w-32 bg-white text-slate-700" data-testid="wb-bulk-assign"><SelectValue placeholder="Assign to" /></SelectTrigger><SelectContent>{staff.map((u) => <SelectItem key={u.id || u.email} value={u.id || u.email}>{u.name || u.email}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" className="h-8 gap-1.5 bg-white text-indigo-700 hover:bg-indigo-50" onClick={() => setSched({ date: new Date().toISOString().slice(0, 10), start_time: "09:00", user: "" })} data-testid="wb-bulk-schedule"><CalendarClock className="w-4 h-4" /> Schedule</Button>
          <button onClick={clearSel} className="p-1.5 hover:bg-indigo-500 rounded" data-testid="wb-bulk-clear"><X className="w-4 h-4" /></button>
        </div>
      )}

      {loading && <div className="flex items-center gap-2 text-slate-400 text-sm mb-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

      {view === "list" && ListView}
      {view === "map" && MapView}
      {view === "split" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="max-h-[70vh] overflow-y-auto">{ListView}</div>
          {MapView}
        </div>
      )}

      {form && <WorkForm item={form} staff={staff} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {confirmBulk && (
        <AlertDialog open onOpenChange={(o) => { if (!o) setConfirmBulk(null); }}>
          <AlertDialogContent data-testid="wb-bulk-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Apply to {selected.size} work item{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
              <AlertDialogDescription>This will {confirmBulk.label} for {selected.size} selected item{selected.size === 1 ? "" : "s"}. You can change it again afterwards.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="wb-bulk-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="wb-bulk-confirm-apply" onClick={() => { bulk(confirmBulk.action, confirmBulk.value); setConfirmBulk(null); }}>Apply</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {addExisting && <AddExistingForm preselect={addExisting.legacy_id} onClose={() => setAddExisting(null)} onOpenExisting={(id) => { setAddExisting(null); openItem(id); }} onSaved={(id) => { setAddExisting(null); load(); if (id) openItem(id); }} />}
      {sched && (
        <div className="fixed inset-0 z-[60] bg-slate-900/40 flex items-center justify-center p-4" onClick={() => setSched(null)} data-testid="wb-schedule-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-4">Schedule {selected.size} item(s)</h3>
            <label className="text-xs font-semibold text-slate-500">Date</label>
            <div className="mb-3"><DatePicker value={sched.date} onChange={(v) => setSched({ ...sched, date: v })} testid="wb-sched-date" /></div>
            <label className="text-xs font-semibold text-slate-500">Start time</label>
            <Input type="time" value={sched.start_time} onChange={(e) => setSched({ ...sched, start_time: e.target.value })} className="mb-3" data-testid="wb-sched-time" />
            <label className="text-xs font-semibold text-slate-500">Assign to</label>
            <Select value={sched.user} onValueChange={(v) => setSched({ ...sched, user: v })}><SelectTrigger className="mb-4" data-testid="wb-sched-user"><SelectValue placeholder="User / engineer" /></SelectTrigger><SelectContent>{staff.map((u) => <SelectItem key={u.id || u.email} value={u.name || u.email}>{u.name || u.email}</SelectItem>)}</SelectContent></Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSched(null)}>Cancel</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={doSchedule} data-testid="wb-sched-confirm">Schedule</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WorkForm({ item, staff, onClose, onSaved }) {
  const editing = !!item.id;
  const [f, setF] = useState({
    title: "", description: "", customer_name: "", address: "", postcode: "", work_type: "",
    department: "", priority: "Medium", status: "New", deadline_date: "", preferred_date: "",
    estimated_minutes: "", staff_required: 1, notes: "", ...item,
  });
  const [busy, setBusy] = useState(false);
  const [tf, setTf] = useState({
    work_type: item.work_type || "", estimated_minutes: item.estimated_minutes || "",
    priority: item.priority || "Medium", deadline_date: item.deadline_date || "",
    preferred_date: item.preferred_date || "", staff_required: item.staff_required || 1,
    required_skills: (item.required_skills || []).join(", "),
    required_roles: (item.required_roles || []).join(", "), notes: item.notes || "",
  });
  const [taskBusy, setTaskBusy] = useState(false);
  const setT = (k, v) => setTf((s) => ({ ...s, [k]: v }));
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const saveTask = async () => {
    setTaskBusy(true);
    const body = {
      work_type: tf.work_type || null,
      estimated_minutes: tf.estimated_minutes ? Number(tf.estimated_minutes) : null,
      priority: tf.priority, deadline_date: tf.deadline_date || null,
      preferred_date: tf.preferred_date || null, staff_required: Number(tf.staff_required) || 1,
      required_skills: tf.required_skills ? tf.required_skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
      required_roles: tf.required_roles ? tf.required_roles.split(",").map((s) => s.trim()).filter(Boolean) : [],
      notes: tf.notes || null,
    };
    try { await api.patch(`/work-bank/${item.id}`, body); toast.success("Planning details updated"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setTaskBusy(false); }
  };
  const save = async () => {
    if (!f.title?.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    const body = { ...f, estimated_minutes: f.estimated_minutes ? Number(f.estimated_minutes) : null, staff_required: Number(f.staff_required) || 1 };
    try {
      if (editing) await api.patch(`/work-bank/${item.id}`, body);
      else await api.post("/work-bank", body);
      toast.success(editing ? "Work updated" : "Work created"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const Field = ({ label, children }) => <div><label className="text-xs font-semibold text-slate-500">{label}</label>{children}</div>;
  if (item.is_task) {
    return (
      <div className="fixed inset-0 z-[60] bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="wb-task-drawer">
        <div className="bg-white h-full w-full sm:max-w-lg shadow-2xl flex flex-col animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 capitalize">{item.source_type} · Job {item.source_id}</div>
              <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
              <p className="text-xs text-slate-500">Master status: {item.master_status || "—"} · Planning: {item.planning_status}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="wb-form-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">Planning entry for master Job {item.source_id}. Customer &amp; site come from the Job; the planning fields below are Job Pool only and never change the Job's status.</div>
            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-xs text-slate-400">Customer</div>{item.customer_name || "—"}</div>
              <div><div className="text-xs text-slate-400">Address</div>{item.address || "—"}</div>
              <div><div className="text-xs text-slate-400">Postcode</div>{item.postcode || "—"}</div>
              <div><div className="text-xs text-slate-400">Map location</div>{item.geo_status === "ok" ? "Mapped" : "Missing"}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Work type"><Input value={tf.work_type} onChange={(e) => setT("work_type", e.target.value)} data-testid="wb-tf-type" /></Field>
              <Field label="Priority"><Select value={tf.priority} onValueChange={(v) => setT("priority", v)}><SelectTrigger data-testid="wb-tf-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Estimated duration (min)"><Input type="number" value={tf.estimated_minutes} onChange={(e) => setT("estimated_minutes", e.target.value)} data-testid="wb-tf-est" /></Field>
              <Field label="Staff required"><Input type="number" value={tf.staff_required} onChange={(e) => setT("staff_required", e.target.value)} data-testid="wb-tf-staff" /></Field>
              <Field label="Required by / deadline"><DatePicker value={tf.deadline_date} onChange={(v) => setT("deadline_date", v)} testid="wb-tf-deadline" /></Field>
              <Field label="Preferred date"><DatePicker value={tf.preferred_date} onChange={(v) => setT("preferred_date", v)} testid="wb-tf-preferred" /></Field>
              <Field label="Required skills (comma)"><Input value={tf.required_skills} onChange={(e) => setT("required_skills", e.target.value)} data-testid="wb-tf-skills" /></Field>
              <Field label="Required roles (comma)"><Input value={tf.required_roles} onChange={(e) => setT("required_roles", e.target.value)} data-testid="wb-tf-roles" /></Field>
            </div>
            <Field label="Planning notes"><textarea rows={2} value={tf.notes} onChange={(e) => setT("notes", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm" data-testid="wb-tf-notes" /></Field>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Scheduled visits</div>
              {(item.visits || []).length === 0 ? <div className="text-slate-400 text-xs">Not scheduled yet.</div> :
                (item.visits || []).map((s, i) => <div key={i} className="text-xs text-slate-600">{s.date} {s.start_time}–{s.end_time} · {(s.staff || []).join(", ") || "unassigned"}</div>)}
            </div>
            <a href={`/tasks/${item.source_id}`} className="inline-block text-indigo-600 text-sm font-medium hover:underline" data-testid="wb-open-job">Open master Job →</a>
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Attachments</div>
              <S3Uploader recordType="work_bank" recordId={item.id} category="Work Bank" serverSide />
            </div>
          </div>
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={taskBusy} onClick={saveTask} data-testid="wb-task-save">{taskBusy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save planning</Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="wb-form">
      <div className="bg-white h-full w-full sm:max-w-lg shadow-2xl flex flex-col animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{editing ? item.work_id : "Add Work"}</h3>
            {editing && <p className="text-xs text-slate-500">{item.status}</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="wb-form-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          <Field label="Title"><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="wb-f-title" /></Field>
          <Field label="Description / instructions"><textarea rows={3} value={f.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm" data-testid="wb-f-desc" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer"><Input value={f.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} data-testid="wb-f-customer" /></Field>
            <Field label="Work type"><Input value={f.work_type || ""} onChange={(e) => set("work_type", e.target.value)} data-testid="wb-f-type" /></Field>
            <Field label="Address"><Input value={f.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="wb-f-address" /></Field>
            <Field label="Postcode"><Input value={f.postcode || ""} onChange={(e) => set("postcode", e.target.value)} data-testid="wb-f-postcode" /></Field>
            <Field label="Department"><Input value={f.department || ""} onChange={(e) => set("department", e.target.value)} data-testid="wb-f-dept" /></Field>
            <Field label="Priority">
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger data-testid="wb-f-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="wb-f-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label="Preferred date"><DatePicker value={f.preferred_date || ""} onChange={(v) => set("preferred_date", v)} testid="wb-f-preferred" /></Field>
            <Field label="Deadline / SLA"><DatePicker value={f.deadline_date || ""} onChange={(v) => set("deadline_date", v)} testid="wb-f-deadline" /></Field>
            <Field label="Est. minutes"><Input type="number" value={f.estimated_minutes || ""} onChange={(e) => set("estimated_minutes", e.target.value)} data-testid="wb-f-est" /></Field>
            <Field label="Staff required"><Input type="number" value={f.staff_required} onChange={(e) => set("staff_required", e.target.value)} data-testid="wb-f-staff" /></Field>
          </div>
          <Field label="Notes"><textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm" data-testid="wb-f-notes" /></Field>
          {editing && item.audit?.length > 0 && (
            <div><label className="text-xs font-semibold text-slate-500">Activity</label>
              <ul className="mt-1 space-y-1.5 border-l-2 border-slate-100 pl-3" data-testid="wb-activity">
                {[...item.audit].reverse().map((a, i) => (
                  <li key={i} className="text-xs text-slate-500"><span className="font-semibold text-slate-700">{a.action}</span> — {a.detail}<div className="text-[10px] text-slate-400">{a.by} · {a.at?.slice(0, 16).replace("T", " ")}</div></li>
                ))}
              </ul>
            </div>
          )}
          {editing && (
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Attachments</div>
              <S3Uploader recordType="work_bank" recordId={item.id} category="Work Bank" serverSide />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="wb-form-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
        </div>
      </div>
    </div>
  );
}

function AddExistingForm({ preselect, onClose, onSaved, onOpenExisting }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    work_type: "", estimated_minutes: "", priority: "Medium", deadline_date: "",
    preferred_date: "", staff_required: 1, required_skills: "", required_roles: "", notes: "",
  });

  const runSearch = useCallback((term) => {
    setSearching(true);
    api.get("/work-bank/jobs/search", { params: { q: term } })
      .then((r) => setResults(r.data.items || [])).catch(() => {}).finally(() => setSearching(false));
  }, []);

  const pick = useCallback((it) => {
    if (it.in_work_bank) { onOpenExisting?.(it.id); return; }
    setJob(it); setF((s) => ({ ...s, work_type: it.work_type || s.work_type }));
  }, [onOpenExisting]);

  useEffect(() => {
    if (preselect) {
      api.get("/work-bank/jobs/search", { params: { q: preselect } })
        .then((r) => { const it = (r.data.items || []).find((x) => x.legacy_id === preselect); if (it) pick(it); else runSearch(preselect); })
        .catch(() => {});
    } else { runSearch(""); }
    // eslint-disable-next-line
  }, [preselect]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!job) return;
    setBusy(true);
    const body = {
      legacy_id: job.legacy_id, work_type: f.work_type || null,
      estimated_minutes: f.estimated_minutes ? Number(f.estimated_minutes) : null,
      priority: f.priority, deadline_date: f.deadline_date || null, preferred_date: f.preferred_date || null,
      staff_required: Number(f.staff_required) || 1,
      required_skills: f.required_skills ? f.required_skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
      required_roles: f.required_roles ? f.required_roles.split(",").map((s) => s.trim()).filter(Boolean) : [],
      notes: f.notes || null,
    };
    try { const r = await api.post("/work-bank/add-existing", body); toast.success(r.data.already ? "Job Pool planning updated" : "Job added to Job Pool"); onSaved(r.data.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };

  const Field = ({ label, children }) => <div><label className="text-xs font-semibold text-slate-500">{label}</label>{children}</div>;
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="wb-existing-form">
      <div className="bg-white h-full w-full sm:max-w-lg shadow-2xl flex flex-col animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Add Existing Job</h3>
            <p className="text-xs text-slate-500">References the Job — the Job itself is never duplicated.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="wb-existing-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {!job ? (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input autoFocus value={q} onChange={(e) => { setQ(e.target.value); runSearch(e.target.value); }} placeholder="Search job ref, title, customer, postcode…" className="pl-9" data-testid="wb-existing-search" />
              </div>
              {searching && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>}
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden" data-testid="wb-existing-results">
                {results.length === 0 && !searching && <div className="p-6 text-center text-slate-400 text-sm">No jobs found.</div>}
                {results.map((it) => (
                  <button key={it.id} onClick={() => pick(it)} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3" data-testid={`wb-existing-result-${it.legacy_id}`}>
                    <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Building2 className="w-4 h-4" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-800 truncate">{it.title || it.legacy_id}</div>
                      <div className="text-xs text-slate-500 truncate">{it.customer_name || "—"} · {it.postcode || "no postcode"} · <span className="font-mono">{it.legacy_id}</span></div>
                    </div>
                    {it.in_work_bank
                      ? <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 shrink-0">Already in Job Pool</span>
                      : <span className="text-[10px] font-medium text-indigo-600 shrink-0">Add →</span>}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="wb-existing-selected">
                <div className="flex items-start justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 capitalize">{job.task_type || "Job"} · {job.legacy_id}</div>
                  <button onClick={() => setJob(null)} className="text-xs text-slate-500 hover:text-slate-800" data-testid="wb-existing-change">Change</button>
                </div>
                <div className="font-semibold text-slate-800 mt-1">{job.title || job.legacy_id}</div>
                <div className="text-xs text-slate-500 mt-0.5">{job.customer_name || "—"} · {job.address || "—"} {job.postcode ? `· ${job.postcode}` : ""}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Work type"><Input value={f.work_type} onChange={(e) => set("work_type", e.target.value)} data-testid="wb-existing-type" /></Field>
                <Field label="Priority"><Select value={f.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger data-testid="wb-existing-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="Estimated duration (min)"><Input type="number" value={f.estimated_minutes} onChange={(e) => set("estimated_minutes", e.target.value)} data-testid="wb-existing-est" /></Field>
                <Field label="Staff required"><Input type="number" value={f.staff_required} onChange={(e) => set("staff_required", e.target.value)} data-testid="wb-existing-staff" /></Field>
                <Field label="Required by / deadline"><DatePicker value={f.deadline_date} onChange={(v) => set("deadline_date", v)} testid="wb-existing-deadline" /></Field>
                <Field label="Preferred date"><DatePicker value={f.preferred_date} onChange={(v) => set("preferred_date", v)} testid="wb-existing-preferred" /></Field>
                <Field label="Required skills (comma)"><Input value={f.required_skills} onChange={(e) => set("required_skills", e.target.value)} data-testid="wb-existing-skills" /></Field>
                <Field label="Required roles (comma)"><Input value={f.required_roles} onChange={(e) => set("required_roles", e.target.value)} data-testid="wb-existing-roles" /></Field>
              </div>
              <Field label="Notes"><textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm" data-testid="wb-existing-notes" /></Field>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!job || busy} onClick={save} data-testid="wb-existing-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Add to Job Pool</Button>
        </div>
      </div>
    </div>
  );
}
